import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import { observeResize } from '../../lib/observeResize';

export interface TimerTextRow {
  text: string;
  size: number;
  color: string;
  className?: string;
  style?: CSSProperties;
}

/** Fit complete rows, then center their natural combined height as one group. */
export function TimerTextStack({ rows, gap = 4 }: { rows: TimerTextRow[]; gap?: number }) {
  const root = useRef<HTMLDivElement>(null);
  const stack = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = root.current;
    const group = stack.current;
    if (!box || !group) return;
    const spans = Array.from(group.querySelectorAll<HTMLSpanElement>('span'));
    const fit = () => {
      const fittedGap = Math.min(gap, box.clientHeight / Math.max(1, rows.length * 2));
      group.style.gap = `${fittedGap}px`;
      const sizes = spans.map((span, index) => {
        const size = rows[index]!.size;
        span.style.fontSize = `${size}px`;
        const fitted = size * Math.min(1, box.clientWidth / Math.max(1, span.scrollWidth));
        span.style.fontSize = `${fitted}px`;
        return fitted;
      });
      const height = spans.reduce((sum, span) => sum + span.getBoundingClientRect().height, 0);
      const scale = Math.min(
        1,
        Math.max(0, box.clientHeight - fittedGap * Math.max(0, rows.length - 1)) /
          Math.max(1, height),
      );
      spans.forEach((span, index) => {
        span.style.fontSize = `${sizes[index]! * scale}px`;
      });
    };
    const stop = observeResize(box, fit);
    let active = true;
    document.fonts?.ready.then(() => {
      if (active) fit();
    });
    return () => {
      active = false;
      stop();
    };
  }, [rows, gap]);
  return (
    <div
      ref={root}
      className="h-full w-full min-h-0 min-w-0 flex items-center justify-center overflow-hidden"
    >
      <div
        ref={stack}
        className="w-full flex flex-col items-center"
        style={{ gap }}
        data-timer-text-stack
      >
        {rows.map((row, index) => (
          <div
            key={index}
            className="w-full min-w-0 flex justify-center"
            title={row.text}
            data-fitted-text
          >
            <span
              className={`block shrink-0 whitespace-nowrap font-bold leading-tight ${row.className ?? ''}`}
              style={{ ...row.style, fontSize: row.size, color: row.color }}
            >
              {row.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
